// Shared building blocks of the Documents feature: the common style sheet, status
// pill, loading / empty / error feedback, pickers, list cards and the request timeline.
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import {
  CatalogItem,
  DocumentStatusTone,
  ParentAdmissionDocument,
  ParentDocument,
  ParentRequestSummary,
  TimelineStep,
} from '../../types';
import { BorderRadius, FontSize, Spacing } from '../../theme/colors';
import {
  documentToneColors,
  formatDocDate,
  formatDocDateTime,
  formatFee,
  hasFee,
  hasTurnaround,
  formatProcessingDays,
  isValidIsoDate,
  lockerStatusMeta,
  primaryRequestAction,
  requestStatusIcon,
  timelineTone,
} from '../../utils/documents';

// ── Shared styles ───────────────────────────────────────────────────────────

/**
 * Styles shared by every documents component, so cards, chips, buttons and banners
 * look the same on the catalogue, the request list, the locker and the detail page.
 * Visual language follows the Early Pickup screen.
 */
const createDocStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    card: {
      backgroundColor: colors.card,
      borderRadius: BorderRadius.md,
      padding: Spacing.md,
      marginBottom: Spacing.md,
      borderWidth: 1,
      borderColor: colors.divider,
      elevation: 1,
      shadowColor: colors.shadow,
      shadowOpacity: 0.05,
      shadowRadius: 6,
      shadowOffset: { width: 0, height: 2 },
    },
    cardMuted: { opacity: 0.85 },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: Spacing.sm,
    },
    cardHeaderText: { flex: 1, minWidth: 0 },
    cardTitle: { fontSize: FontSize.lg, fontWeight: '700', color: colors.textPrimary },
    cardSubtitle: { fontSize: FontSize.sm, color: colors.textSecondary, marginTop: 2 },
    cardBody: { fontSize: FontSize.sm, color: colors.textSecondary, marginTop: Spacing.sm, lineHeight: 19 },
    iconBox: { width: 38, height: 38, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
    metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: Spacing.md, marginTop: Spacing.sm },
    metaItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    metaText: { fontSize: FontSize.sm, color: colors.textSecondary, fontWeight: '500' },
    cardFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: Spacing.sm,
      marginTop: Spacing.md,
      paddingTop: Spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.divider,
    },
    footerText: { flex: 1, fontSize: 11, color: colors.textSecondary },

    intro: { fontSize: FontSize.sm, color: colors.textSecondary, marginBottom: Spacing.md, lineHeight: 19 },
    introStrong: { fontWeight: '700', color: colors.textPrimary },

    sectionTitle: {
      fontSize: FontSize.xs,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: colors.textSecondary,
      marginBottom: Spacing.sm,
    },

    chipRow: { gap: Spacing.sm, paddingBottom: Spacing.md },
    chip: {
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: BorderRadius.full,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    chipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
    chipText: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textSecondary },
    chipTextActive: { color: '#FFFFFF' },

    primaryBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      backgroundColor: colors.primary,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      paddingVertical: 10,
      minHeight: 40,
    },
    primaryBtnText: { color: '#FFFFFF', fontSize: FontSize.sm, fontWeight: '700' },
    outlineBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      paddingVertical: 9,
      minHeight: 40,
    },
    outlineBtnText: { color: colors.primary, fontSize: FontSize.sm, fontWeight: '700' },
    dangerOutlineBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.danger,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      paddingVertical: 9,
      minHeight: 40,
    },
    dangerOutlineBtnText: { color: colors.danger, fontSize: FontSize.sm, fontWeight: '700' },
    btnDisabled: { opacity: 0.55 },

    bigPrimaryBtn: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: colors.primary,
      borderRadius: BorderRadius.sm,
      paddingVertical: 14,
      minHeight: 48,
    },
    bigSecondaryBtn: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1.5,
      borderColor: colors.border,
      borderRadius: BorderRadius.sm,
      paddingVertical: 14,
      minHeight: 48,
    },
    bigSecondaryBtnText: { color: colors.textSecondary, fontSize: FontSize.md, fontWeight: '700' },
    bigPrimaryBtnText: { color: '#FFFFFF', fontSize: FontSize.md, fontWeight: '700' },
    bigDangerBtn: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.danger,
      borderRadius: BorderRadius.sm,
      paddingVertical: 14,
      minHeight: 48,
    },

    banner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: Spacing.sm,
      borderRadius: BorderRadius.sm,
      padding: Spacing.md,
      marginBottom: Spacing.md,
    },
    bannerText: { flex: 1, fontSize: FontSize.sm, color: colors.textPrimary, lineHeight: 19 },
    bannerTitle: { fontSize: FontSize.sm, fontWeight: '800', color: colors.textPrimary, marginBottom: 2 },
    retryText: { fontSize: FontSize.sm, fontWeight: '700', color: colors.primary },

    empty: { alignItems: 'center', paddingVertical: 48, paddingHorizontal: Spacing.lg },
    emptyTitle: {
      fontSize: FontSize.lg,
      fontWeight: '600',
      color: colors.textSecondary,
      marginTop: Spacing.md,
      textAlign: 'center',
    },
    emptyHint: {
      fontSize: FontSize.sm,
      color: colors.textMuted,
      textAlign: 'center',
      marginTop: Spacing.sm,
      lineHeight: 19,
    },

    label: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textPrimary, marginBottom: 8 },
    required: { color: colors.danger },
    helperText: { fontSize: FontSize.sm, color: colors.textSecondary, marginTop: 6, lineHeight: 18 },
    errorText: { fontSize: FontSize.sm, color: colors.danger, marginTop: 6, fontWeight: '600' },
    input: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      paddingVertical: 12,
      fontSize: FontSize.md,
      color: colors.textPrimary,
      backgroundColor: colors.background,
    },
    inputError: { borderColor: colors.danger, borderWidth: 1.5 },
    textArea: { minHeight: 96, textAlignVertical: 'top' },
    counter: { alignSelf: 'flex-end', fontSize: FontSize.xs, color: colors.textMuted, marginTop: 4 },

    modalOverlay: {
      flex: 1,
      backgroundColor: colors.overlay,
      justifyContent: 'center',
      alignItems: 'center',
      padding: Spacing.md,
    },
    dialog: {
      backgroundColor: colors.card,
      borderRadius: BorderRadius.md,
      padding: Spacing.lg,
      width: '100%',
      maxWidth: 420,
    },
    dialogTitle: { fontSize: FontSize.lg, fontWeight: '800', color: colors.textPrimary, marginBottom: Spacing.sm },
    dialogActions: { flexDirection: 'row', gap: Spacing.sm, marginTop: Spacing.md },
  });

/** The theme colours plus the shared documents style sheet, memoised per theme. */
export function useDocStyles() {
  const { colors } = useTheme();
  const s = useMemo(() => createDocStyles(colors), [colors]);
  return { colors, s };
}

// ── Status pill ─────────────────────────────────────────────────────────────

/** Words + icon + colour, so the status never depends on colour alone. */
export function StatusPill({ label, tone, icon }: { label: string; tone: DocumentStatusTone | string; icon?: string }) {
  const { colors } = useTheme();
  const palette = useMemo(() => documentToneColors(colors, tone), [colors, tone]);
  return (
    <View
      style={[pillStyles.pill, { backgroundColor: palette.bg, borderColor: palette.fg }]}
      accessibilityLabel={`Status: ${label}`}>
      {!!icon && <FeatherIcon name={icon} size={12} color={palette.fg} />}
      <Text style={[pillStyles.text, { color: palette.fg }]} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

const pillStyles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 4,
    flexShrink: 0,
    maxWidth: 170,
  },
  text: {
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    flexShrink: 1,
  },
});

// ── Feedback: error / info banners, empty state, loader ────────────────────

export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { colors, s } = useDocStyles();
  return (
    <View
      style={[s.banner, { backgroundColor: colors.dangerLight }]}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite">
      <FeatherIcon name="alert-circle" size={16} color={colors.danger} />
      <Text style={s.bannerText}>{message}</Text>
      {!!onRetry && (
        <TouchableOpacity onPress={onRetry} accessibilityRole="button" accessibilityLabel="Retry">
          <Text style={s.retryText}>Retry</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

export function InfoBanner({
  title,
  message,
  icon = 'info',
  tone = 'info',
}: {
  title?: string;
  message: string;
  icon?: string;
  tone?: 'info' | 'warning' | 'danger' | 'success';
}) {
  const { colors, s } = useDocStyles();
  const palette = {
    info: { fg: colors.primary, bg: colors.primaryLight },
    warning: { fg: colors.warning, bg: colors.warningLight },
    danger: { fg: colors.danger, bg: colors.dangerLight },
    success: { fg: colors.success, bg: colors.successLight },
  }[tone];
  return (
    <View style={[s.banner, { backgroundColor: palette.bg }]}>
      <FeatherIcon name={icon} size={16} color={palette.fg} style={{ marginTop: 1 }} />
      <View style={{ flex: 1 }}>
        {!!title && <Text style={s.bannerTitle}>{title}</Text>}
        <Text style={s.bannerText}>{message}</Text>
      </View>
    </View>
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  actionLabel,
  onAction,
}: {
  icon: string;
  title: string;
  hint?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const { colors, s } = useDocStyles();
  return (
    <View style={s.empty}>
      <FeatherIcon name={icon} size={40} color={colors.textMuted} />
      <Text style={s.emptyTitle}>{title}</Text>
      {!!hint && <Text style={s.emptyHint}>{hint}</Text>}
      {!!actionLabel && !!onAction && (
        <TouchableOpacity
          style={[s.primaryBtn, { marginTop: 16 }]}
          onPress={onAction}
          accessibilityRole="button">
          <Text style={s.primaryBtnText}>{actionLabel}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

/** The standard loader (common/LoadingSpinner), with what is loading. */
export function CenteredLoader({ message }: { message?: string }) {
  return <LoadingSpinner message={message} />;
}

// ── Option sheet ────────────────────────────────────────────────────────────

/**
 * A themed single-choice list, same shape as settings/OptionPickerModal but reading
 * colours from useTheme so it works in dark mode, and case-insensitive on the
 * selected value (the server stores the configured spelling).
 */
export function OptionSheet({
  visible,
  title,
  options,
  selected,
  onSelect,
  onClose,
}: {
  visible: boolean;
  title: string;
  options: string[];
  selected: string;
  onSelect: (value: string) => void;
  onClose: () => void;
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => createSheetStyles(colors), [colors]);
  const selectedLower = selected.trim().toLowerCase();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={onClose}
        accessibilityLabel="Close options">
        <TouchableOpacity activeOpacity={1} style={styles.sheet}>
          <View style={styles.header}>
            <Text style={styles.title} numberOfLines={2}>
              {title}
            </Text>
            <TouchableOpacity
              onPress={onClose}
              style={styles.closeBtn}
              accessibilityRole="button"
              accessibilityLabel="Close">
              <FeatherIcon name="x" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={options}
            keyExtractor={item => item}
            renderItem={({ item }) => {
              const isSelected = item.toLowerCase() === selectedLower;
              return (
                <TouchableOpacity
                  style={[styles.optionRow, isSelected && styles.optionRowSelected]}
                  onPress={() => {
                    onSelect(item);
                    onClose();
                  }}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: isSelected }}
                  accessibilityLabel={item}>
                  <Text style={[styles.optionText, isSelected && styles.optionTextSelected]}>
                    {item}
                  </Text>
                  {isSelected && <FeatherIcon name="check" size={18} color={colors.primary} />}
                </TouchableOpacity>
              );
            }}
          />
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const createSheetStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: colors.overlay,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    sheet: {
      backgroundColor: colors.card,
      borderRadius: 20,
      paddingBottom: 8,
      width: '100%',
      maxWidth: 400,
      maxHeight: '75%',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.divider,
      gap: 12,
    },
    title: { flex: 1, fontSize: 16, fontWeight: '800', color: colors.textPrimary },
    closeBtn: { padding: 4 },
    optionRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 15,
      borderBottomWidth: 1,
      borderBottomColor: colors.divider,
      gap: 12,
    },
    optionRowSelected: { backgroundColor: colors.primaryLight },
    optionText: { flex: 1, fontSize: 15, fontWeight: '600', color: colors.textPrimary },
    optionTextSelected: { color: colors.primary },
  });

// ── Date picker (native) ────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A themed calendar that hands back yyyy-MM-dd built from local parts. Unlike
 * common/DatePickerModal it never round-trips through `new Date('yyyy-MM-dd')`
 * (parsed as UTC, which lands on the previous day west of Greenwich), allows past
 * dates (a date of leaving or of birth is usually in the past) and has a year jump.
 */
export function DocDatePicker({
  visible,
  title,
  value,
  onSelect,
  onClose,
}: {
  visible: boolean;
  title: string;
  /** yyyy-MM-dd or ''. */
  value: string;
  onSelect: (value: string) => void;
  onClose: () => void;
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => createCalendarStyles(colors), [colors]);

  const initial = useMemo(() => {
    if (value && isValidIsoDate(value)) {
      const [y, m] = value.split('-').map(Number);
      return { year: y, month: m - 1 };
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  }, [value]);

  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);

  // Re-centre on the chosen value each time the picker opens.
  useEffect(() => {
    if (visible) {
      setYear(initial.year);
      setMonth(initial.month);
    }
  }, [visible, initial]);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const shiftMonth = (delta: number) => {
    const next = month + delta;
    if (next < 0) {
      setMonth(11);
      setYear(y => y - 1);
    } else if (next > 11) {
      setMonth(0);
      setYear(y => y + 1);
    } else {
      setMonth(next);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <Text style={styles.title} numberOfLines={2}>
            {title}
          </Text>

          <View style={styles.navRow}>
            <TouchableOpacity
              onPress={() => setYear(y => y - 1)}
              style={styles.navBtn}
              accessibilityLabel="Previous year">
              <FeatherIcon name="chevrons-left" size={18} color={colors.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => shiftMonth(-1)}
              style={styles.navBtn}
              accessibilityLabel="Previous month">
              <FeatherIcon name="chevron-left" size={18} color={colors.primary} />
            </TouchableOpacity>
            <Text style={styles.monthTitle} accessibilityRole="header">
              {MONTH_NAMES[month]} {year}
            </Text>
            <TouchableOpacity
              onPress={() => shiftMonth(1)}
              style={styles.navBtn}
              accessibilityLabel="Next month">
              <FeatherIcon name="chevron-right" size={18} color={colors.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setYear(y => y + 1)}
              style={styles.navBtn}
              accessibilityLabel="Next year">
              <FeatherIcon name="chevrons-right" size={18} color={colors.primary} />
            </TouchableOpacity>
          </View>

          <View style={styles.weekRow}>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <Text key={`${d}-${i}`} style={styles.weekDay}>
                {d}
              </Text>
            ))}
          </View>

          <View style={styles.grid}>
            {cells.map((day, index) => {
              if (!day) return <View key={`blank-${index}`} style={styles.cell} />;
              const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
              const isSelected = dateStr === value;
              const isToday = dateStr === todayStr;
              return (
                <TouchableOpacity
                  key={dateStr}
                  style={[styles.cell, isSelected && styles.cellSelected, isToday && !isSelected && styles.cellToday]}
                  onPress={() => onSelect(dateStr)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected }}
                  accessibilityLabel={`${day} ${MONTH_NAMES[month]} ${year}`}>
                  <Text style={[styles.cellText, isSelected && styles.cellTextSelected]}>{day}</Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <View style={styles.actions}>
            <TouchableOpacity style={styles.secondaryBtn} onPress={onClose} accessibilityRole="button">
              <Text style={styles.secondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => onSelect(todayStr)}
              accessibilityRole="button">
              <Text style={styles.primaryText}>Today</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createCalendarStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: colors.overlay,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    modal: {
      backgroundColor: colors.card,
      borderRadius: 20,
      padding: 16,
      width: '100%',
      maxWidth: 360,
    },
    title: { fontSize: 15, fontWeight: '800', color: colors.textPrimary, marginBottom: 8 },
    navRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
    navBtn: { padding: 8 },
    monthTitle: {
      flex: 1,
      textAlign: 'center',
      fontSize: 15,
      fontWeight: '700',
      color: colors.textPrimary,
    },
    weekRow: { flexDirection: 'row', marginBottom: 4 },
    weekDay: {
      width: '14.28%',
      textAlign: 'center',
      fontSize: 12,
      fontWeight: '600',
      color: colors.textSecondary,
    },
    grid: { flexDirection: 'row', flexWrap: 'wrap' },
    cell: {
      width: '14.28%',
      aspectRatio: 1,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 999,
    },
    cellSelected: { backgroundColor: colors.primary },
    cellToday: { borderWidth: 1, borderColor: colors.primary },
    cellText: { fontSize: 13, fontWeight: '500', color: colors.textPrimary },
    cellTextSelected: { color: '#FFFFFF', fontWeight: '800' },
    actions: { flexDirection: 'row', gap: 8, marginTop: 12 },
    secondaryBtn: {
      flex: 1,
      borderWidth: 1.5,
      borderColor: colors.border,
      borderRadius: 12,
      paddingVertical: 12,
      alignItems: 'center',
    },
    secondaryText: { fontSize: 14, fontWeight: '600', color: colors.textSecondary },
    primaryBtn: {
      flex: 1,
      backgroundColor: colors.primary,
      borderRadius: 12,
      paddingVertical: 12,
      alignItems: 'center',
    },
    primaryText: { fontSize: 14, fontWeight: '700', color: '#FFFFFF' },
  });

// ── Cards: catalogue, my requests, my documents ────────────────────────────

export function CatalogCard({
  item,
  onRequest,
  onOpenRequest,
}: {
  item: CatalogItem;
  onRequest: (item: CatalogItem) => void;
  onOpenRequest: (requestId: string) => void;
}) {
  const { colors, s } = useDocStyles();
  const hasOpen = !!item.openRequestNumber;
  const blocked = !item.canRequest && !hasOpen;

  const content = (
    <>
      <View style={s.cardHeader}>
        <View style={[s.iconBox, { backgroundColor: colors.primaryLight }]}>
          <FeatherIcon name="file-text" size={18} color={colors.primary} />
        </View>
        <View style={s.cardHeaderText}>
          <Text style={s.cardTitle}>{item.name}</Text>
          <Text style={s.cardSubtitle}>{item.categoryLabel}</Text>
        </View>
        {item.canRequest && <FeatherIcon name="chevron-right" size={20} color={colors.textMuted} />}
      </View>

      {!!item.description && (
        <Text style={s.cardBody} numberOfLines={3}>
          {item.description}
        </Text>
      )}

      {(hasTurnaround(item.processingDays) || hasFee(item.fee)) && (
        <View style={s.metaRow}>
          {hasTurnaround(item.processingDays) && (
            <View style={s.metaItem}>
              <FeatherIcon name="clock" size={13} color={colors.textSecondary} />
              <Text style={s.metaText}>{formatProcessingDays(item.processingDays)}</Text>
            </View>
          )}
          {hasFee(item.fee) && (
            <View style={s.metaItem}>
              <FeatherIcon name="tag" size={13} color={colors.textSecondary} />
              <Text style={s.metaText}>{formatFee(item.fee)}</Text>
            </View>
          )}
        </View>
      )}

      {hasOpen && (
        <TouchableOpacity
          style={[s.banner, { backgroundColor: colors.primaryLight, marginTop: 12, marginBottom: 0, alignItems: 'center' }]}
          onPress={() => item.openRequestId && onOpenRequest(item.openRequestId)}
          disabled={!item.openRequestId}
          accessibilityRole="link"
          accessibilityLabel={`Already requested, ${item.openRequestNumber}. Open the request.`}>
          <FeatherIcon name="info" size={14} color={colors.primary} />
          <Text style={[s.bannerText, { color: colors.primary, fontWeight: '600' }]}>
            Already requested · {item.openRequestNumber}
          </Text>
          <FeatherIcon name="arrow-right" size={14} color={colors.primary} />
        </TouchableOpacity>
      )}

      {blocked && (
        <View style={[s.banner, { backgroundColor: colors.warningLight, marginTop: 12, marginBottom: 0 }]}>
          <FeatherIcon name="slash" size={14} color={colors.warning} style={{ marginTop: 2 }} />
          <View style={{ flex: 1 }}>
            <Text style={s.bannerTitle}>{item.isEligible ? 'Not available right now' : 'Not eligible'}</Text>
            {(item.ineligibleReasons.length > 0
              ? item.ineligibleReasons
              : ['The school has not opened this document for requests.']
            ).map(reason => (
              <Text key={reason} style={s.bannerText}>
                • {reason}
              </Text>
            ))}
          </View>
        </View>
      )}

      {item.canRequest && (
        <View style={[s.cardFooter, { justifyContent: 'flex-end' }]}>
          <View style={s.primaryBtn}>
            <FeatherIcon name="plus" size={14} color="#FFFFFF" />
            <Text style={s.primaryBtnText}>Request</Text>
          </View>
        </View>
      )}
    </>
  );

  if (item.canRequest) {
    return (
      <TouchableOpacity
        style={s.card}
        onPress={() => onRequest(item)}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel={[
          `Request ${item.name}.`,
          hasTurnaround(item.processingDays) ? `${formatProcessingDays(item.processingDays)}.` : '',
          hasFee(item.fee) ? `${formatFee(item.fee)}.` : '',
        ]
          .filter(Boolean)
          .join(' ')}>
        {content}
      </TouchableOpacity>
    );
  }
  return <View style={[s.card, blocked && s.cardMuted]}>{content}</View>;
}

export function RequestCard({
  item,
  busy,
  onOpen,
  onDownload,
}: {
  item: ParentRequestSummary;
  busy?: boolean;
  onOpen: (item: ParentRequestSummary) => void;
  onDownload: (item: ParentRequestSummary) => void;
}) {
  const { colors, s } = useDocStyles();
  const action = primaryRequestAction(item);

  return (
    <TouchableOpacity
      style={s.card}
      onPress={() => onOpen(item)}
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel={`${item.documentName} for ${item.studentName}, ${item.requestNumber}, ${item.statusLabel}. Open details.`}>
      <View style={s.cardHeader}>
        <View style={s.cardHeaderText}>
          <Text style={s.cardTitle}>{item.documentName}</Text>
          <Text style={s.cardSubtitle}>
            {item.studentName} · {item.requestNumber}
          </Text>
        </View>
        <StatusPill label={item.statusLabel} tone={item.statusTone} icon={requestStatusIcon(item.status)} />
      </View>

      {!!item.purpose && (
        <Text style={s.cardBody} numberOfLines={2}>
          {item.purpose}
        </Text>
      )}

      <View style={s.metaRow}>
        <View style={s.metaItem}>
          <FeatherIcon name="calendar" size={13} color={colors.textSecondary} />
          <Text style={s.metaText}>Requested {formatDocDate(item.requestedAt)}</Text>
        </View>
        {!!item.expectedBy && !item.canDownload && item.status !== 'REJECTED' && item.status !== 'CANCELLED' && (
          <View style={s.metaItem}>
            <FeatherIcon name="clock" size={13} color={colors.textSecondary} />
            <Text style={s.metaText}>Expected {formatDocDate(item.expectedBy)}</Text>
          </View>
        )}
        {item.fee > 0 && (
          <View style={s.metaItem}>
            <FeatherIcon name="tag" size={13} color={colors.textSecondary} />
            <Text style={s.metaText}>{formatFee(item.fee)}</Text>
          </View>
        )}
      </View>

      <View style={[s.cardFooter, { justifyContent: 'flex-end' }]}>
        {action === 'download' ? (
          <TouchableOpacity
            style={[s.primaryBtn, busy && s.btnDisabled]}
            onPress={() => onDownload(item)}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel={`Download ${item.documentName}`}>
            {busy ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <FeatherIcon name="download" size={14} color="#FFFFFF" />
            )}
            <Text style={s.primaryBtnText}>Download</Text>
          </TouchableOpacity>
        ) : action === 'update' ? (
          <TouchableOpacity
            style={[s.primaryBtn, { backgroundColor: colors.warning }]}
            onPress={() => onOpen(item)}
            accessibilityRole="button"
            accessibilityLabel={`Update ${item.documentName}`}>
            <FeatherIcon name="edit-3" size={14} color="#FFFFFF" />
            <Text style={s.primaryBtnText}>Update</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={s.outlineBtn}
            onPress={() => onOpen(item)}
            accessibilityRole="button"
            accessibilityLabel={`View ${item.documentName}`}>
            <Text style={s.outlineBtnText}>View</Text>
            <FeatherIcon name="chevron-right" size={14} color={colors.primary} />
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );
}

export function LockerCard({
  doc,
  busyKey,
  onView,
  onDownload,
  onOpenRequest,
}: {
  doc: ParentDocument;
  busyKey: string | null;
  onView: (doc: ParentDocument) => void;
  onDownload: (doc: ParentDocument) => void;
  onOpenRequest: (requestId: string) => void;
}) {
  const { colors, s } = useDocStyles();
  const meta = lockerStatusMeta(doc.status);
  const viewing = busyKey === `open:${doc.id}`;
  const downloading = busyKey === `save:${doc.id}`;

  return (
    <View style={[s.card, !doc.canDownload && s.cardMuted]}>
      <View style={s.cardHeader}>
        <View style={[s.iconBox, { backgroundColor: doc.canDownload ? colors.successLight : colors.divider }]}>
          <FeatherIcon name="award" size={18} color={doc.canDownload ? colors.success : colors.textMuted} />
        </View>
        <View style={s.cardHeaderText}>
          <Text style={s.cardTitle}>{doc.documentName}</Text>
          <Text style={s.cardSubtitle}>
            {doc.studentName} · {doc.documentNumber}
          </Text>
        </View>
        <StatusPill label={doc.statusLabel} tone={meta.tone} icon={meta.icon} />
      </View>

      <View style={s.metaRow}>
        <View style={s.metaItem}>
          <FeatherIcon name="calendar" size={13} color={colors.textSecondary} />
          <Text style={s.metaText}>Issued {formatDocDate(doc.issuedAt)}</Text>
        </View>
        {!!doc.expiryDate && (
          <View style={s.metaItem}>
            <FeatherIcon name="clock" size={13} color={colors.textSecondary} />
            <Text style={s.metaText}>
              {doc.status === 'EXPIRED' ? 'Expired' : 'Valid until'} {formatDocDate(doc.expiryDate)}
            </Text>
          </View>
        )}
      </View>

      <View style={s.cardFooter}>
        <TouchableOpacity
          onPress={() => onOpenRequest(doc.requestId)}
          accessibilityRole="link"
          accessibilityLabel={`Open request ${doc.requestNumber}`}
          style={{ flex: 1 }}>
          <Text style={[s.footerText, { color: colors.primary, fontWeight: '600' }]} numberOfLines={1}>
            {doc.requestNumber}
          </Text>
        </TouchableOpacity>
        {doc.canDownload ? (
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity
              style={[s.outlineBtn, viewing && s.btnDisabled]}
              onPress={() => onView(doc)}
              disabled={viewing || downloading}
              accessibilityRole="button"
              accessibilityLabel={`View ${doc.documentName}`}>
              {viewing ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <FeatherIcon name="eye" size={14} color={colors.primary} />
              )}
              <Text style={s.outlineBtnText}>View</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.primaryBtn, downloading && s.btnDisabled]}
              onPress={() => onDownload(doc)}
              disabled={viewing || downloading}
              accessibilityRole="button"
              accessibilityLabel={`Download ${doc.documentName}`}>
              {downloading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <FeatherIcon name="download" size={14} color="#FFFFFF" />
              )}
              <Text style={s.primaryBtnText}>Download</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <Text style={[s.footerText, { flex: 0, textAlign: 'right' }]}>
            {doc.status === 'REVOKED' ? 'Revoked by the school' : 'No longer valid'}
          </Text>
        )}
      </View>
    </View>
  );
}

// ── Admission document card ─────────────────────────────────────────────────

const ADMISSION_STATUS: Record<string, { tone: DocumentStatusTone; icon: string }> = {
  VERIFIED: { tone: 'success', icon: 'check-circle' },
  REASSIGNED: { tone: 'warning', icon: 'alert-circle' },
  PENDING: { tone: 'info', icon: 'clock' },
};

function formatFileSize(bytes?: number | null): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A document the family uploaded at admission, with the school's review status. */
export function AdmissionDocumentCard({
  doc,
  busyKey,
  onView,
  onDownload,
}: {
  doc: ParentAdmissionDocument;
  busyKey: string | null;
  onView: (doc: ParentAdmissionDocument) => void;
  onDownload: (doc: ParentAdmissionDocument) => void;
}) {
  const { colors, s } = useDocStyles();
  const meta = ADMISSION_STATUS[doc.status] ?? ADMISSION_STATUS.PENDING;
  const viewing = busyKey === `open:${doc.id}`;
  const downloading = busyKey === `save:${doc.id}`;
  const details = [doc.studentName, doc.fileType, formatFileSize(doc.sizeBytes)].filter(Boolean).join(' · ');

  return (
    <View style={s.card}>
      <View style={s.cardHeader}>
        <View style={[s.iconBox, { backgroundColor: colors.divider }]}>
          <FeatherIcon name="file-text" size={18} color={colors.primary} />
        </View>
        <View style={s.cardHeaderText}>
          <Text style={s.cardTitle}>{doc.documentName}</Text>
          {!!details && <Text style={s.cardSubtitle}>{details}</Text>}
        </View>
        <StatusPill label={doc.statusLabel} tone={meta.tone} icon={meta.icon} />
      </View>

      <View style={[s.cardFooter, { justifyContent: 'flex-end' }]}>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <TouchableOpacity
            style={[s.outlineBtn, viewing && s.btnDisabled]}
            onPress={() => onView(doc)}
            disabled={viewing || downloading}
            accessibilityRole="button"
            accessibilityLabel={`View ${doc.documentName}`}>
            {viewing ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <FeatherIcon name="eye" size={14} color={colors.primary} />
            )}
            <Text style={s.outlineBtnText}>View</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[s.primaryBtn, downloading && s.btnDisabled]}
            onPress={() => onDownload(doc)}
            disabled={viewing || downloading}
            accessibilityRole="button"
            accessibilityLabel={`Download ${doc.documentName}`}>
            {downloading ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <FeatherIcon name="download" size={14} color="#FFFFFF" />
            )}
            <Text style={s.primaryBtnText}>Download</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

// ── Request timeline ────────────────────────────────────────────────────────

const STEP_ICON: Record<string, string> = {
  DONE: 'check',
  CURRENT: 'more-horizontal',
  FAILED: 'x',
  PENDING: '',
};

const STEP_WORDS: Record<string, string> = {
  DONE: 'done',
  CURRENT: 'in progress',
  FAILED: 'stopped',
  PENDING: 'not started',
};

/** The request's journey: done, current, pending or failed steps with dates and notes. */
export function RequestTimeline({ steps }: { steps: TimelineStep[] }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createTimelineStyles(colors), [colors]);

  return (
    <View accessibilityRole="list">
      {steps.map((step, index) => {
        const tone = documentToneColors(colors, timelineTone(step.state));
        const isLast = index === steps.length - 1;
        const pending = step.state === 'PENDING';
        const icon = STEP_ICON[step.state] ?? '';
        return (
          <View
            key={`${step.label}-${index}`}
            style={styles.row}
            accessible
            accessibilityLabel={`${step.label}, ${STEP_WORDS[step.state] ?? step.state.toLowerCase()}${
              step.at ? `, ${formatDocDateTime(step.at)}` : ''
            }${step.note ? `. ${step.note}` : ''}`}>
            <View style={styles.rail}>
              <View
                style={[
                  styles.dot,
                  pending
                    ? { borderColor: colors.border, backgroundColor: colors.card }
                    : { borderColor: tone.fg, backgroundColor: step.state === 'CURRENT' ? tone.bg : tone.fg },
                ]}>
                {!!icon && (
                  <FeatherIcon name={icon} size={12} color={step.state === 'CURRENT' ? tone.fg : '#FFFFFF'} />
                )}
              </View>
              {!isLast && (
                <View
                  style={[styles.line, { backgroundColor: step.state === 'DONE' ? colors.success : colors.border }]}
                />
              )}
            </View>
            <View style={[styles.content, isLast && styles.contentLast]}>
              <Text
                style={[
                  styles.label,
                  pending && styles.labelPending,
                  step.state === 'CURRENT' && { color: colors.primary },
                  step.state === 'FAILED' && { color: colors.danger },
                ]}>
                {step.label}
              </Text>
              {!!step.at && <Text style={styles.at}>{formatDocDateTime(step.at)}</Text>}
              {!!step.note && <Text style={styles.note}>{step.note}</Text>}
            </View>
          </View>
        );
      })}
    </View>
  );
}

const createTimelineStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    row: { flexDirection: 'row' },
    rail: { width: 28, alignItems: 'center' },
    dot: {
      width: 22,
      height: 22,
      borderRadius: 11,
      borderWidth: 2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    line: { width: 2, flex: 1, minHeight: 18, marginVertical: 2 },
    content: { flex: 1, paddingLeft: Spacing.sm, paddingBottom: Spacing.md },
    contentLast: { paddingBottom: 0 },
    label: { fontSize: FontSize.md, fontWeight: '700', color: colors.textPrimary, marginTop: 1 },
    labelPending: { color: colors.textMuted, fontWeight: '600' },
    at: { fontSize: FontSize.sm, color: colors.textSecondary, marginTop: 2 },
    note: { fontSize: FontSize.sm, color: colors.textPrimary, marginTop: 4, lineHeight: 18 },
  });
